import { useCallback, useEffect, useState } from "react";
import Button from "./Button";
import FormInput from "./FormInput";
import operatorService from "../services/operatorService";

const formatMoney = (value) => new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(value || 0);
const creditCode = c => `${String(c.nivelRefinanciacion || 0).padStart(2, "0")}-${String(c.codigoDirecto || 0).padStart(4, "0")}`;
const today = () => {
  const date = new Date();
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

function OperatorPaymentPanel({ operator, financial, onUpdated }) {
  const [payments, setPayments] = useState([]);
  const [monto, setMonto] = useState("");
  const [fechaPago, setFechaPago] = useState(today);
  const [credito, setCredito] = useState("");
  const [historyError, setHistoryError] = useState("");
  const [loading, setLoading] = useState(true);
  const [observaciones, setObservaciones] = useState("");
  const [saving, setSaving] = useState(false);

  const loadPayments = useCallback(async () => {
    setLoading(true);
    try {
      setPayments(await operatorService.getPayments(operator._id));
      setHistoryError("");
    } catch (error) { setHistoryError("No se pudo consultar el historial de pagos."); }
    finally { setLoading(false); }
  }, [operator._id]);

  useEffect(() => { loadPayments(); }, [loadPayments]);

  const handleSave = async () => {
    if (!monto || Number(monto) <= 0) return alert("Ingresá un monto válido");
    setSaving(true);
    try {
      await operatorService.registerPayment({ operador: operator._id, credito: credito || undefined, monto: Number(monto), fechaPago, observaciones });
      setMonto(""); setObservaciones("");
      await loadPayments();
      onUpdated();
    } catch (error) { alert(error.response?.data?.error || "No se pudo registrar el pago"); }
    finally { setSaving(false); }
  };

  return (
    <div className="cc-operator-payments">
      <h3>Comisiones y pagos · {operator.nombre} {operator.apellido}</h3>
      <p>Al alcanzar el 50% abonado se genera media comisión; al completar el crédito se genera la totalidad. Refinanciar conserva la media comisión generada. El pago al operador se registra aparte.</p>
      {!financial ? <p role="status">Resumen de comisiones no disponible. Consultá el estado de carga arriba.</p> : <>
      <div className="cc-operator-finance-summary">
        <div><span>Comisión prevista de los créditos</span><strong>{formatMoney(financial.totalPrevisto)}</strong></div>
        <div><span>Comisión generada</span><strong>{formatMoney(financial?.totalComisiones)}</strong></div>
        <div><span>Pagado al operador</span><strong className="cc-number-green">{formatMoney(financial?.totalPagado)}</strong></div>
        <div><span>Pendiente</span><strong className="cc-number-danger">{formatMoney(financial?.pendiente)}</strong></div>
      </div>
      {financial.saldoAFavor > 0 && <p>Pagado por adelantado: {formatMoney(financial.saldoAFavor)}</p>}
      <h4>Detalle por crédito</h4>
      {(financial.detalleComisiones || []).length === 0 ? <p>No hay créditos asociados a este operador.</p> : financial.detalleComisiones.map(c => (
        <div className="cc-operator-payment-row" key={c._id}>
          <strong>{creditCode(c)} · {c.cliente?.nombre} {c.cliente?.apellido}</strong>
          <span>{c.estado}</span>
          <small>Comisión pactada: {c.porcentajeComision ?? 0}% · Prevista: {formatMoney(c.prevista)} · Generada: {formatMoney(c.generada)}{c.comisionTotal === 0 ? " · Este crédito tiene comisión guardada en cero." : ""}</small>
        </div>
      ))}
      </>}
      <div className="cc-operator-payment-form">
        <div>
          <label className="cc-form-label" htmlFor="operator-credit">Crédito relacionado (opcional)</label>
          <select id="operator-credit" className="cc-select" value={credito} onChange={e => setCredito(e.target.value)}>
            <option value="">Pago general al operador</option>
            {(financial?.detalleComisiones || []).filter(c => c.estado !== "CANCELADO").map(c => <option key={c._id} value={c._id}>{creditCode(c)} · {c.cliente?.nombre} {c.cliente?.apellido}</option>)}
          </select>
        </div>
        <FormInput label="Monto pagado" type="number" thousandSeparator placeholder="25000" value={monto} onChange={(e) => setMonto(e.target.value)} />
        <FormInput label="Fecha de pago" type="date" value={fechaPago} onChange={(e) => setFechaPago(e.target.value)} />
        <FormInput label="Observaciones" placeholder="Transferencia, efectivo..." value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
        <Button onClick={handleSave} disabled={saving}>{saving ? "Guardando..." : "Registrar pago al operador"}</Button>
      </div>
      <h4>Historial de pagos</h4>
      {historyError ? <div role="alert"><p>{historyError}</p><Button onClick={loadPayments}>Reintentar historial</Button></div> : loading ? <p>Cargando historial…</p> : payments.length === 0 ? <p className="cc-empty-inline">Todavía no hay pagos registrados.</p> : payments.map((payment) => <div className="cc-operator-payment-row" key={payment._id}><strong>{formatMoney(payment.monto)}</strong><span>{new Date(payment.fechaPago).toLocaleDateString("es-AR")}</span><small>{payment.credito ? `${creditCode(payment.credito)} · ${payment.credito.cliente?.nombre || ""} · ` : "Pago general · "}{payment.observaciones || "Sin observaciones"}</small></div>)}
    </div>
  );
}

export default OperatorPaymentPanel;
